import { Injectable, Optional } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { BetslipDisplayMode } from '@frontend/sports/types/components/bet-placement';

import { BetslipType } from '../../../core/betslip-type';
import { BetslipV2ParticipantPick } from '../../../core/picks/betslip-v2-participant-pick';
import { PickId } from '../../../core/picks/pick-id';
import { IPrice, PickOddsState, PriceType } from '../../../core/picks/pick-models';
import { OverAskBet, OverAskMarketTypeChange } from '../../../model/over-ask/over-ask';
import { hasBetBuilderBetPickStake, hasSingleBetPickStake } from '../../betplacement/services/stake/linear-stake-utils';
import { getPickEachWayInfo, getPickPriceIds, isStartingPriceSelected, isXCastPick } from '../../picks/services/betslip-pick-helpers';
import { IBetslipPickState } from '../../picks/state';
import { IRewardToken } from '../../reward-tokens/reward-tokens.model';
import { TokensRecord } from '../../reward-tokens/state';
import { IBetslipTypeState } from '../../types/state';
import BetValidationService from '../../validation/services/composition/bets/bet-validation.service';
import {
    BetslipGeneratorContextType,
    CombinationRestrictions,
    IBetslipCalculatorOverrides,
    IBetslipCalculatorPicksOverride,
    IBetslipCalculatorSlipOverride,
    ICalculatorInput,
    emptyBetslipCalculatorOverrides,
} from '../models';
import { SlipType } from '../slip-type';
import { BetslipStateInput, BetslipStateOverrides } from './base-input-builder';
import BetBuilderBetInputBuilder from './bet-builder-bet-input-builder';
import BetslipBarInputBuilderService from './betslip-bar-input-builder.service';
import ComboBetInputBuilder from './combo-bet-input-builder';
import EditBetInputBuilder from './edit-bet-input-builder';
import MultiReturnsMessageInputBuilderService from './multi-returns-message-input-builder.service';
import SingleBetInputBuilder from './single-bet-input-builder';
import SystemBetInputBuilder from './system-bet/system-bet-input-builder';
import TeaserBetInputBuilder from './teaser-bet-input-builder';

/**
 * This class converts a BetslipState to a Calculator Input.
 */
@Injectable()
export class BetslipGeneratorInputBuilder {
    constructor(
        private singleBetInputBuilder: SingleBetInputBuilder,
        private comboBetInputBuilder: ComboBetInputBuilder,
        private systemBetInputBuilder: SystemBetInputBuilder,
        private editBetInputBuilder: EditBetInputBuilder,
        private betBuilderBetInputBuilder: BetBuilderBetInputBuilder,
        private betValidation: BetValidationService,
        @Optional() private teaserBetInputBuilder: TeaserBetInputBuilder,
        @Optional() private multiReturnsMessageInputBuilderService: MultiReturnsMessageInputBuilderService | null,
        @Optional() private betslipBarInputBuilderService: BetslipBarInputBuilderService | null,
        private betslipConfig: BetslipConfig,
    ) {}

    getCalculatorInput(betslipState: BetslipStateInput): ICalculatorInput {
        return this.getCalculatorInputForType(betslipState, betslipState.types.base.currentSelectedType);
    }

    getCalculatorInputForType(betslipState: BetslipStateInput, betslipType: BetslipType | null): ICalculatorInput {
        if (betslipType === null) {
            return { betslip: [] };
        }

        switch (betslipType) {
            case BetslipType.Single:
                // When single bet type is selected we generate multiple slips
                return this.getSingleBetsCalculatorInput(betslipState);
            case BetslipType.Combo:
                // When combo bet type is selected we generate 1 slips
                return this.getComboBetsCalculatorInput(betslipState);
            case BetslipType.System:
                // When system bet type is selected we generate 1 slips

                if (betslipState.base.displayMode === BetslipDisplayMode.Linear) {
                    const linearSystems = Object.values(betslipState.types.systemBet.linearTypeStakes).map((s) => s.slipType);

                    return this.getSystemSlipsCalculatorInput(betslipState, linearSystems);
                }

                return { betslip: Array.from(this.systemBetInputBuilder.getBetGeneratorInput(betslipState)) };
            case BetslipType.EditBet:
                // Edit bet generates only one slip
                return { betslip: Array.from(this.editBetInputBuilder.getBetGeneratorInput(betslipState)) };
            case BetslipType.Teaser:
                return this.getTeaserCalculatedInput(betslipState);
            case BetslipType.BetBuilder:
                // When BetBuilder bet type is selected we generate multiple slips
                return this.getBetBuilderBetsCalculatorInput(betslipState);
            default:
                throw new Error('TODO implement other types');
        }
    }

    getSingleBetsCalculatorInputForPick(betslipState: BetslipStateInput, pickId: PickId): ICalculatorInput {
        const selector = (state: BetslipStateInput) =>
            Object.entries(state.types.singleBet.picks).filter(
                ([key, value]) => key === pickId.toString() && value.isSelected && state.picks.pickList.some((p) => p.id.toString() === key),
            );

        return {
            betslip: [...this.singleBetInputBuilder.getBetGeneratorInput(betslipState, { picksSelector: selector })],
        };
    }

    getBetBuilderBetsCalculatorInput(betslipState: BetslipStateInput, pickId?: PickId): ICalculatorInput {
        const selector = (state: BetslipStateInput) =>
            Object.entries(state.types.betBuilder.picks).filter(
                ([key, value]) =>
                    key === (pickId?.toString() ?? key) && value.isSelected && state.picks.pickList.some((p) => p.id.toString() === key),
            );

        return {
            betslip: [...this.betBuilderBetInputBuilder.getBetGeneratorInput(betslipState, { picksSelector: selector })],
        };
    }

    getBetBuilderBetsCalculatorInputForSummary(betslipState: BetslipStateInput, pickId?: PickId): ICalculatorInput {
        const selector = (state: BetslipStateInput) =>
            Object.entries(state.types.betBuilder.picks).filter(
                ([key, value]) =>
                    key === (pickId?.toString() ?? key) &&
                    value.isSelected &&
                    state.picks.pickList.some((p) => p.id.toString() === key) &&
                    hasBetBuilderBetPickStake(value, state.rewardTokens.tokens),
            );

        return {
            betslip: [...this.betBuilderBetInputBuilder.getBetGeneratorInput(betslipState, { picksSelector: selector })],
        };
    }

    getSingleBetsCalculatorInput(betslipState: BetslipStateInput): ICalculatorInput {
        return {
            betslip: [...this.singleBetInputBuilder.getBetGeneratorInput(betslipState)],
        };
    }

    getLinearSingleBetsCalculatorInput(betslipState: BetslipStateInput): ICalculatorInput {
        return {
            betslip: [
                ...this.singleBetInputBuilder.getBetGeneratorInput(betslipState, {
                    picksSelector: (input: BetslipStateInput) =>
                        Object.entries(input.types.singleBet.picks).filter(
                            ([key, value]) =>
                                value.isSelected &&
                                !value.isLocked &&
                                input.picks.pickList.some((p) => p.id.toString() === key) &&
                                hasSingleBetPickStake(value, input.rewardTokens.tokens),
                        ),
                }),
            ],
        };
    }

    getComboBetsCalculatorInput(betslipState: BetslipStateInput): ICalculatorInput {
        return {
            betslip: [
                ...this.comboBetInputBuilder.getBetGeneratorInput(betslipState, {
                    isSportcastAsComboEnabled: this.betslipConfig.isSportcastAsComboEnabled,
                }),
            ],
        };
    }

    getSystemSlipsCalculatorInput(betslipState: BetslipStateInput, systems: SlipType[]): ICalculatorInput {
        return {
            betslip: [...systems.flatMap((system) => Array.from(this.systemBetInputBuilder.getBetGeneratorInput(betslipState, system)))],
        };
    }

    getTeaserCalculatedInput(betslipState: BetslipStateInput): ICalculatorInput {
        return { betslip: Array.from(this.teaserBetInputBuilder.getBetGeneratorInput(betslipState.types.teaserBet, betslipState)) };
    }

    getOverrides(summaryContext: BetslipGeneratorContextType, betslipState: BetslipStateOverrides): IBetslipCalculatorOverrides {
        switch (summaryContext) {
            case BetslipGeneratorContextType.EditBetSwap: {
                return this.editBetInputBuilder.getEditBetSwapPickOverride(betslipState);
            }
            case BetslipGeneratorContextType.MultiReturnMessage: {
                if (this.multiReturnsMessageInputBuilderService === null) {
                    throw new Error(
                        `MultiReturnsMessage context is invalid. It's either not supported on the current platform or you forgot to register it.`,
                    );
                }

                return this.multiReturnsMessageInputBuilderService.getMultiReturnsMessageOverrides(betslipState);
            }
            case BetslipGeneratorContextType.BetslipBar: {
                if (this.betslipBarInputBuilderService === null) {
                    throw new Error(`BetslipBar context is invalid. It's either not supported on the current platform or you forgot to register it.`);
                }

                return this.betslipBarInputBuilderService.getBetslipBarOverrides(betslipState, this.betslipConfig.isSportcastAsComboEnabled);
            }
        }

        return emptyBetslipCalculatorOverrides;
    }

    getRestrictions(
        summaryContextType: BetslipGeneratorContextType,
        betslipState: { picks: IBetslipPickState; types: IBetslipTypeState },
    ): CombinationRestrictions {
        switch (summaryContextType) {
            case BetslipGeneratorContextType.Betslip:
            case BetslipGeneratorContextType.AccaBoost:
            case BetslipGeneratorContextType.MultiReturnMessage:
            case BetslipGeneratorContextType.BetslipLineSwitcher:
            case BetslipGeneratorContextType.BetslipBar:
            case BetslipGeneratorContextType.BetBuilder: {
                const isSystemBet = betslipState.types.base.currentSelectedType === BetslipType.System;
                const isTeaserBet = betslipState.types.base.currentSelectedType === BetslipType.Teaser;

                return this.betValidation.getRestrictions(betslipState.picks.pickList, isSystemBet, isTeaserBet);
            }
            case BetslipGeneratorContextType.EditBetSwap: {
                // Edit bet doesn't support systems
                return this.betValidation.getRestrictions(betslipState.picks.pickList, false, false);
            }
            default: {
                throw new Error('Invalid summary context');
            }
        }
    }

    getRestrictionsForType(
        summaryContextType: BetslipGeneratorContextType,
        betslipState: { picks: IBetslipPickState; types: IBetslipTypeState },
        betslipType: BetslipType | null,
    ): CombinationRestrictions {
        switch (summaryContextType) {
            case BetslipGeneratorContextType.Betslip:
            case BetslipGeneratorContextType.AccaBoost:
            case BetslipGeneratorContextType.MultiReturnMessage:
            case BetslipGeneratorContextType.BetslipLineSwitcher:
            case BetslipGeneratorContextType.BetslipBar:
            case BetslipGeneratorContextType.BetBuilder: {
                const isSystemBet = betslipType === BetslipType.System;
                const isTeaserBet = betslipType === BetslipType.Teaser;

                return this.betValidation.getRestrictions(betslipState.picks.pickList, isSystemBet, isTeaserBet);
            }
            case BetslipGeneratorContextType.EditBetSwap: {
                // Edit bet doesn't support systems
                return this.betValidation.getRestrictions(betslipState.picks.pickList, false, false);
            }
            default: {
                throw new Error('Invalid summary context');
            }
        }
    }

    getOverAskInput(overAskBets: OverAskBet[], tokens: TokensRecord): ICalculatorInput {
        return {
            betslip: [
                ...overAskBets.map((b) => {
                    return {
                        slipId: b.slipId,
                        system: b.type,
                        stake: b.stake.toNumber(),
                        rewardToken: this.getOverAskToken(b, tokens),
                        items: b.picks.map((overAskPick) => {
                            return {
                                pick: overAskPick.pick,
                                priceIds: getPickPriceIds(overAskPick.pick),
                                price: overAskPick.oddsState === PickOddsState.Open ? overAskPick.price : null,
                                isBanker: overAskPick.isBanker,
                                eachWayInfo: overAskPick.isEachWay ? getPickEachWayInfo(overAskPick.pick) : null,
                                isXCast: isXCastPick(overAskPick.pick),
                                isStartingPrice: isStartingPriceSelected(overAskPick.pick),
                            };
                        }),
                    };
                }),
            ],
        };
    }

    createOverAskCalculatorOverrides(overAskBets: OverAskBet[]): IBetslipCalculatorOverrides {
        const slipOverrides = [] as IBetslipCalculatorSlipOverride[];
        const pickOverrides: IBetslipCalculatorPicksOverride = {
            replacements: {},
            eachWay: {},
            prices: {},
        };
        for (const bet of overAskBets) {
            if (bet.changes.stake) {
                slipOverrides.push({
                    system: bet.type,
                    pickIds: bet.picks.map((p) => p.pick.id.toString()),
                    stake: {
                        value: bet.changes.stake.toNumber(),
                    },
                });
            }
            for (const overAskPick of bet.picks) {
                const pickId = overAskPick.pick.id.toString();
                if (overAskPick.changes.price) {
                    const price = overAskPick.changes.price as IPrice;
                    if (BetslipV2ParticipantPick.isPick(overAskPick.pick) && overAskPick.price.type !== overAskPick.changes.price.type) {
                        pickOverrides.replacements![pickId] = {
                            price,
                            pick: overAskPick.pick,
                            isBanker: overAskPick.isBanker,
                            priceIds: overAskPick.pick.participants.length > 1 ? overAskPick.pick.prices.map((p) => p.id) : [price.id],
                            eachWayInfo: overAskPick.isEachWay ? getPickEachWayInfo(overAskPick.pick) : null,
                            isXCast: isXCastPick(overAskPick.pick),
                            isStartingPrice: overAskPick.changes.price
                                ? overAskPick.changes.price.type === PriceType.StartingPrice
                                : isStartingPriceSelected(overAskPick.pick),
                        };
                    } else {
                        pickOverrides.prices![pickId] = { ...overAskPick.changes.price };
                    }
                }
                if (overAskPick.changes.marketType) {
                    if (overAskPick.changes.marketType === OverAskMarketTypeChange.EachWay) {
                        pickOverrides.eachWay![pickId] = getPickEachWayInfo(overAskPick.pick);
                    } else {
                        pickOverrides.eachWay![pickId] = null;
                    }
                }
                if (bet.isRemoved) {
                    pickOverrides.replacements![pickId] = null;
                }
            }
        }

        return {
            slipOverrides,
            pickOverrides,
        };
    }

    private getOverAskToken(bet: OverAskBet, tokens: TokensRecord): IRewardToken | null {
        if (bet.promoTokens.length > 0) {
            return tokens[bet.promoTokens[0].userTokenId] || null;
        }

        if (bet.edsPromoTokens.length > 0) {
            return tokens[bet.edsPromoTokens[0].userTokenId] || null;
        }

        return null;
    }
}
